import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";

import { currencyAmount } from "../../../helpers/TemplateHelper";
import ItemUsageDialog from "../../components/ItemUsageDialog";

const ItemUsage = () => {
  const toast = useRef(null);
  const dt = useRef(null);

  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [userRole, setUserRole] = useState(null);

  const [height, setHeight] = useState(window.innerHeight - 320);

  function decodeJwt(token) {
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid JWT format");
    }

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  }

  useEffect(() => {
    fetchItems();

    const token = localStorage.getItem("token");

    if (token) {
      const decodedPayload = decodeJwt(token);
      const role = decodedPayload.role;
      setUserRole(role);
    }

    const handleResize = () => {
      setHeight(window.innerHeight - 320);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get("/api/itemUsage");
      setItems(response.data);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch items. Please try again.",
        life: 3000,
      });
    }
  };

  const confirmStatus = async (rowData, isEnabled) => {
    try {
      const updatedData = { ...rowData, status: isEnabled ? 1 : 0 };
      const response = await axios.post(`/api/GoodReceives`, updatedData);

      if (response.status === 200 || response.status === 201) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `${isEnabled ? "Activated" : "Deactivated"} successfully`,
          life: 3000,
        });

        const updatedIndex = items.findIndex((item) => item.id === rowData.id);
        if (updatedIndex !== -1) {
          items[updatedIndex] = updatedData;
          setItems([...items]);
        }
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "An error occurred while changing status. Please try again.",
        life: 3000,
      });
    }
  };

  const openNewDialog = () => {
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
  };

  const onItemUsageSubmit = () => {
    hideDialog();
    fetchItems();
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const statusTemplate = (rowData) => {
    const handleStatus = (isEnabled) => {
      confirmDialog({
        message: `Are you sure you want to ${
          isEnabled ? "activate" : "deactivate"
        } this item?`,
        header: "Confirmation",
        icon: "pi pi-exclamation-triangle",
        accept: () => confirmStatus(rowData, isEnabled),
      });
    };
    return (
      <React.Fragment>
        {userRole === "admin" && (
          <div className="p-field-checkbox">
            <Checkbox
              inputId={`statusCheckbox_${rowData.id}`}
              checked={rowData.status === 1}
              onChange={(e) => handleStatus(e.checked)}
              tooltip={rowData.status ? "Deactivate" : "Activate"}
            />
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="card flex flex-column gap-4">
      <Toast ref={toast} position="bottom-right" />
      <div className="flex justify-content-between align-items-center">
        <div>
          <h4 className="text-primary mb-1">Item Usage</h4>
          <label className="text-secondary">Add, edit, view item usage</label>
        </div>
        <div className="flex gap-2 align-items-center">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              type="search"
              onInput={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search"
            />
          </span>
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            severity="help"
            onClick={fetchItems}
          />
          <Button
            label="Export"
            icon="pi pi-file-excel"
            severity="success"
            onClick={exportCSV}
          />
          <Button
            label="Add Record"
            icon="pi pi-plus"
            className="p-button-primary"
            onClick={() => openNewDialog(null)}
          />
        </div>
      </div>
      <DataTable
        ref={dt}
        value={items}
        globalFilter={globalFilter}
        scrollable
        scrollHeight={height}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
      >
        <Column field="item.code" header="Item Code" sortable />
        <Column field="item.name" header="Name" sortable />
        <Column
          field="unitPrice"
          header="Purchase Price"
          className="text-right"
          sortable
          body={(rowData) => `${currencyAmount(rowData.unitPrice)} LKR`}
        />
        <Column
          field="quantity"
          header="Quantity"
          sortable
          className="text-right"
          body={(rowData) => `${currencyAmount(rowData.quantity)}`}
        />
        <Column field="remark" header="Remark" sortable />
        <Column
          sortable
          field="updated"
          header="Updated Date"
          body={(row) => <span>{new Date(row.updated).toDateString()}</span>}
        />
        <Column sortable field="updatedBy" header="Updated By" />
        <Column field="status" header="Status" body={statusTemplate} sortable />
      </DataTable>
      <Dialog
        visible={dialogVisible}
        onHide={hideDialog}
        header="Add, edit Item Usage"
        style={{ width: "500px" }}
      >
        <ItemUsageDialog
          item={selectedItem}
          onHide={hideDialog}
          onSubmit={onItemUsageSubmit}
        />
      </Dialog>
    </div>
  );
};

export default ItemUsage;
